<template>
    <div class="modal-full">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Edit Blog Post</h1>
        </div>

        <div class="modal-body no-pad" v-if="post">
            <div class="flex h-full">
                <div class="account-sidebar h-full">
                    <div class="mb-4 text-center">
                        <span class="fa-layers fa-4x mb-3">
                            <i class="fa fa-circle"></i>
                            <i class="fas fa-user fa-inverse" data-fa-transform="shrink-6.5"></i>
                        </span>

                        <p class="text-lg font-light"></p>
                    </div>

                    <div class="mb-3">
                        <label>ID</label>
                        <p>{{ post.blog_id }}</p>

                        <label>Status</label>
                        <p class="text-green font-bold" v-if="post.blog_active == 1">Published</p>
                        <p class="text-red-500 font-bold" v-if="post.blog_active != 1">Unpublished</p>

                        <label>Views</label>
                        <p>{{ post.blog_views }}</p>

                        <label>URL</label>
                        <p><a :href="postUrl" target="_blank" class="break-words">{{ postUrl }}</a></p>

                        <hr>

                        <label>Social Preview</label>
                        <div class="border rounded bg-white">
                            <div class="border-b" v-if="metaImageUrl">
                                <img :src="metaImageUrl" class="block w-full rounded-t"
                                     style="height: 150px; object-fit: cover;">
                            </div>
                            <div class="p-3">
                                <div class="font-bold mb-2 truncate">
                                    {{ this.post.blog_meta_title || this.post.blog_title }}
                                </div>
                                <div class="mb-2">{{ postSummaryText }}</div>
                                <div class="text-gray">journolink.com</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="account-body">
                    <form class="profile-form">
                        <div class="form-field">
                            <input type="text" v-model="post.blog_title" class="text-lg font-bold">
                            <label>Title</label>
                        </div>

                        <div class="form-field">
                            <input type="text" v-model="post.blog_slug">
                            <label>URL Slug</label>
                        </div>

                        <div class="form-field">
                            <select v-model="post.blog_category">
                                <option v-for="(category, index) in categories" :key="index" :value="category.cat_id">
                                    {{ category.cat_name }}
                                </option>
                            </select>
                            <label>Category</label>
                        </div>

                        <div class="form-field styled">
                            <label>Publish Date/Time</label>

                            <simple-date-time-picker
                                v-model="post.blog_datetime">
                            </simple-date-time-picker>
                        </div>

                        <div class="form-field">
                            <select v-model="post.blog_active">
                                <option value="0">No</option>
                                <option value="1">Yes</option>
                            </select>
                            <label>Active/Public</label>
                        </div>

                        <div class="form-field">
                            <textarea rows="3" v-model="post.blog_summary"></textarea>
                            <label>Summary Text</label>
                        </div>

                        <div class="form-field styled">
                            <label>Content</label>

                            <Editor
                                :init="{
                                    ...wysiwygOptions,
                                    plugins: wysiwygPlugins
                                }"
                                api-key="kxkhkjfta11crbajh0in2mcap04z7nt4czo9icice5ihpd8n"
                                v-model="post.blog_content"
                                rows="10"
                            >
                            </Editor>
                        </div>

                        <div class="form-field styled">
                            <label>Featured Image</label>
                            <image-drop
                                class="w-full sm:w-1/2 md:w-1/4"
                                name="blog_image"
                                :upload-url="imageUploadUrl"
                                :value="post.blog_image"
                                target="blog_image"
                                instance="BlogPost"
                                :instance-id="post.blog_id"
                                response-data-path="path"
                                @input="handleFeaturedImageUpdate"
                            ></image-drop>
                        </div>

                        <div class="form-field styled">
                            <label>Meta (Social) Image</label>
                            <image-drop
                                name="blog_image"
                                class="w-full sm:w-1/2 md:w-1/4"
                                :upload-url="imageUploadUrl"
                                :value="post.blog_meta_image"
                                target="blog_image"
                                instance="BlogPost"
                                :instance-id="post.blog_id"
                                response-data-path="path"
                                @input="handleMetaImageUpdate"
                            ></image-drop>
                        </div>

                        <div class="form-field styled">
                            <label>Original Article URL</label>
                            <input type="text" v-model="post.blog_canonical">
                            <p class="text-xs text-gray-600 mt-1">If this content is being duplicated from another
                                source, paste the link here to prevent search engines from penalising us for duplicate
                                content.</p>
                        </div>

                        <div class="form-field">
                            <input type="text" v-model="post.blog_meta_title">
                            <label>Meta &amp; Social Title</label>
                        </div>

                        <div class="form-field">
                            <textarea rows="3" v-model="post.blog_meta_description"></textarea>
                            <label>Meta &amp; Social Description</label>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <div class="flex">
                <div class="left text-left">
                    <button class="btn-danger muted" type="button" @click="handleDelete" v-if="post">Delete</button>
                </div>

                <div class="right text-right">
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="saveForm">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'

export default {
    name: 'BlogView',
    props: {
        //
    },
    components: {
        Editor,
    },
    data() {
        return {
            post: null,
            categories: [],
        }
    },
    computed: {
        /**
         * Return the image upload URL - must accept a single request parameter named "file"
         *
         * @return {String}
         */
        imageUploadUrl() {
            return route('blogs.image', this.post.blog_id)
        },
        /**
         * Return the posts URL
         *
         * @return {String}
         */
        postUrl() {
            return 'https://journolink.com/blog/' + this.post.blog_id + '-' + this.post.blog_slug
        },
        /**
         * Retrieve the text content for the post social summary
         *
         * @return {String}
         */
        postSummaryText() {
            if (this.post.blog_meta_description !== null && this.post.blog_meta_description !== '') {
                return this.post.blog_meta_description.substr(0, 120)
            }

            return this.post.blog_summary.substr(0, 120)
        },
        /**
         * Return the relevant meta image for the post
         *
         * @return {String}
         */
        metaImageUrl() {
            if (this.post.blog_meta_image !== null) {
                return this.post.blog_meta_image
            }

            if (this.post.blog_image !== null) {
                return this.post.blog_image
            }

            return null
        },
    },
    mounted() {
        this.loadData()

        this.setMetaTitle('Edit Blog Post | JournoLink Control')
    },
    beforeDestroy() {
        //
    },
    beforeRouteUpdate(to, from, next) {
        this.$route.params.blogId = to.params.blogId
        this.loadData()
        next()
    },
    methods: {
        loadData() {
            axios.get(route('blogs.show', this.$route.params.blogId))
                .then(response => {
                    this.post = response.data.post
                    this.categories = response.data.categories
                    this.setMetaTitle('Edit Blog Post | JournoLink Control')
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.$root.goBack()
        },
        /**
         *
         */
        saveForm() {
            axios.put(route('blogs.update', this.$route.params.blogId), this.formatPostForSave(this.post))
                .then(response => {
                    this.post = response.data.post
                    this.$bus.$emit('alert.success', 'Blog Post has been saved')
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Handle a featured image upload
         *
         * @param  {Object} image
         * @return {void}
         */
        handleFeaturedImageUpdate(image) {
            axios.put(route('blogs.update', this.$route.params.blogId), {
                blog_image: image
            })
                .then(response => {
                    this.post = response.data.post
                    this.$bus.$emit('alert.success', 'Featured image has been updated')
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Handle a meta image upload
         *
         * @param  {Object} image
         * @return {void}
         */
        handleMetaImageUpdate(image) {
            axios.put(route('blogs.update', this.$route.params.blogId), {
                blog_meta_image: image
            })
                .then(response => {
                    this.post = response.data.post
                    this.$bus.$emit('alert.success', 'Meta image has been updated')
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Handle a button-press to delete the Post
         *
         * @return {Object}
         */
        handleDelete() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'Are you sure you wish to delete this blog post?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('blogs.delete', this.post.blog_id))
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Blog post has been deleted')
                                this.$router.push({name: 'blog.list'})
                            })
                            .catch((e) => {
                                console.dir({e})
                            })
                    }
                })
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
        /**
         * Format the post object for save
         *
         * @param  {Object} post
         * @return {Object}
         */
        formatPostForSave(post) {
            let thisPost = _.clone(post)
            thisPost.blog_datetime = moment(thisPost.blog_datetime).format('YYYY-MM-DD HH:mm:ss')

            return thisPost
        },
    },
    watch: {
        post: {
            handler(to) {
                // The date/time picker component requires a ISO formatted string, so we'll
                // convert this here, rather than amending the format in the controller
                if (!moment(to.blog_datetime, 'YYYY-MM-DDTHH:mm:ss', true).isValid()) {
                    this.post.blog_datetime = moment(to.blog_datetime).toISOString(true)
                }
            },
            deep: true,
        },
    },
}
</script>

<style lang="less">
.account-sidebar {
    padding: 15px;
    width: 350px;
    font-size: 0.9rem;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.account-activity {
    width: 250px;
    font-size: 0.9rem;
    padding: 15px;
}

.hidden-input {
    input {
        display: none;
        width: 100%;
    }

    .show {
        display: inline-block;
    }

    .hide {
        display: none;
    }

    .edit-field-link {
        text-decoration-line: underline;
        text-decoration-style: solid;
        color: #222222;
        word-wrap: break-word;
    }
}

.profile-form {
    .section-title {
        margin-top: 20px;
    }

    .form-title {
        margin-bottom: 20px;
    }
}
</style>
